import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Brain, Layers, Image as ImageIcon, Database, BookOpen } from 'lucide-react';
import { Badge } from '../../../../../components/ui/Badge';
import { IssueRow, MODULE_CONFIG } from '../shared';
import type { UnifiedQuestionRow } from '../hooks/useGovernanceData';
interface QuestionDetailPanelProps {
  row: UnifiedQuestionRow;
}
export function QuestionDetailPanel({
  row
}: QuestionDetailPanelProps) {
  const modConfig = MODULE_CONFIG[row.moduleCode] || {
    bgClass: 'bg-gray-500',
    textClass: 'text-gray-700'
  };
  return <div className="px-4 py-4 bg-gray-50 border-t border-gray-100" data-id="element-5171">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5172">
        {/* Left Column: Pipeline Issues */}
        <div className="space-y-4" data-id="element-5173">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-5174">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-5175" /> Pipeline Issues
          </h4>

          {row.pipelineIssues.length === 0 ? <div className="p-4 bg-white rounded-lg border border-gray-200 text-center" data-id="element-5176">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" data-id="element-5177" />
              <p className="text-sm text-gray-600" data-id="element-5178">
                Geen pipeline issues gevonden.
              </p>
            </div> : <div className="space-y-2" data-id="element-5179">
              {row.pipelineIssues.map((issue, idx) => <IssueRow key={idx} issue={issue} data-id="element-5180" />)}
            </div>}
        </div>

        {/* Right Column: Metadata & Quality */}
        <div className="space-y-6" data-id="element-5181">
          {/* Quality & Metadata */}
          <div data-id="element-5182">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5183">
              <Brain className="w-3.5 h-3.5" data-id="element-5184" /> Kwaliteit & Metadata
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4" data-id="element-5185">
              <div className="grid grid-cols-2 gap-4" data-id="element-5186">
                <div data-id="element-5187">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5188">Cognitief Niveau</p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-5189">
                    Niveau {row.cognitiveLevel}
                  </p>
                </div>
                <div data-id="element-5190">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5191">Module & Tier</p>
                  <div className="flex items-center gap-1.5" data-id="element-5192">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${modConfig.bgClass} text-white`} data-id="element-5193">
                      {row.moduleCode}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-5194">
                      Tier {row.tier}
                    </span>
                  </div>
                </div>
                <div data-id="element-5195">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5196">Vraagtype</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-5197">
                    {row.questionType}
                  </p>
                </div>
                <div data-id="element-5198">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5199">Skill</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-5200">
                    {row.skill}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Seed Status */}
          <div data-id="element-5201">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5202">
              <Database className="w-3.5 h-3.5" data-id="element-5203" /> Seed Status
            </h4>

            {row.seedWarnings.length === 0 ? <div className="p-3 bg-white rounded-lg border border-gray-200 flex items-center gap-2" data-id="element-5204">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-5205" />
                <span className="text-sm text-gray-600" data-id="element-5206">
                  In sync met taxonomy
                </span>
              </div> : <div className="space-y-2" data-id="element-5207">
                {row.seedWarnings.map((warn, i) => <div key={i} className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800 font-mono" data-id="element-5208">
                    {warn}
                  </div>)}
              </div>}
          </div>

          {/* Visual Status */}
          <div data-id="element-5209">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5210">
              <ImageIcon className="w-3.5 h-3.5" data-id="element-5211" /> Visuele Hulpmiddelen
            </h4>

            <div className="p-3 bg-white rounded-lg border border-gray-200" data-id="element-5212">
              {row.hasVisualProblems ? <div className="flex items-start gap-2 text-red-700" data-id="element-5213">
                  <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5214" />
                  <span className="text-sm" data-id="element-5215">
                    Er zijn problemen met de visuals (zie pipeline issues).
                  </span>
                </div> : <div className="flex items-start gap-2 text-emerald-700" data-id="element-5216">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5217" />
                  <span className="text-sm" data-id="element-5218">
                    Visuals zijn geldig en correct geconfigureerd.
                  </span>
                </div>}
            </div>
          </div>

          {/* Pedagogisch Contract */}
          <div data-id="element-5219">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5220">
              <BookOpen className="w-3.5 h-3.5" data-id="element-5221" /> Pedagogisch Contract
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3" data-id="element-5222">
              {(() => {
              const pedagogyIssues = row.pipelineIssues.filter(i => i.domain === 'pedagogy');
              const hasPedagogyBlockers = pedagogyIssues.some(i => i.kind === 'blocker');
              const hasPedagogyCoaches = pedagogyIssues.some(i => i.kind === 'coach');
              return <>
                    <div className="grid grid-cols-2 gap-3" data-id="element-5223">
                      <div data-id="element-5224">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-5225">DOK Niveau</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-5226">
                          {row.pipelineIssues.some(i => i.code === 'SEED_DOK_MISSING') ? <span className="text-amber-600" data-id="element-5227">
                              Niet ingesteld
                            </span> : 'Ingesteld'}
                        </p>
                      </div>
                      <div data-id="element-5228">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-5229">
                          Misconceptie Tags
                        </p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-5230">
                          {pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_UNTAGGED') ? <span className="text-amber-600" data-id="element-5231">Ontbreekt</span> : pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_PARTIAL') ? <span className="text-amber-500" data-id="element-5232">Gedeeltelijk</span> : <span className="text-emerald-600" data-id="element-5233">Compleet</span>}
                        </p>
                      </div>
                    </div>
                    {pedagogyIssues.length === 0 ? <div className="flex items-center gap-2 pt-1" data-id="element-5234">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-5235" />
                        <span className="text-sm text-gray-600" data-id="element-5236">
                          Pedagogisch contract voldoet aan alle eisen.
                        </span>
                      </div> : <div className="flex items-center gap-2 pt-1" data-id="element-5237">
                        {hasPedagogyBlockers ? <XCircle className="w-4 h-4 text-red-500" data-id="element-5238" /> : <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-5239" />}
                        <span className="text-sm text-gray-600" data-id="element-5240">
                          {pedagogyIssues.length} pedagogische{' '}
                          {pedagogyIssues.length === 1 ? 'bevinding' : 'bevindingen'}
                          {hasPedagogyBlockers ? ' (blocker)' : hasPedagogyCoaches ? ' (verbeterpunt)' : ''}
                        </span>
                      </div>}
                  </>;
            })()}
            </div>
          </div>
        </div>
      </div>
    </div>;
}